<template>
  <div>
    <table id="activity_log" class="table w-full mt-6" aria-label="List of activity logs.">
      <thead>
      <tr v-if="availableFilters === null">
        <th colspan="7">
          <div class="text-center">
            <loader class="text-60"/>
          </div>
        </th>
      </tr>
      <tr v-else>
        <th class="w-20"></th>
        <th>
          <select
            slot="select"
            :value="filters.status"
            class="block w-full form-control-sm form-select"
            @change="event => onFilterChange({ status: event.target.value })"
          >
            <option v-for="(option, idx) in availableFilters.statuses" :key="idx" :value="option">
              {{ option === '—' ? '—' : trans(`activity.status.${option}`) }}
            </option>
          </select>
        </th>
        <th>
          <v-date-picker v-model.range.number="range" mode="dateTime" :popover="{visibility: 'click'}" is-range is24hr>
            <template v-slot="{ inputValue, inputEvents }">
              <input
                v-on="inputEvents.start"
                :value="inputValue.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                class="bg-white form-control-sm form-input px-2 py-1 rounded"
              />
            </template>
          </v-date-picker>
        </th>
        <th>
          <select
            slot="select"
            :value="filters.direction"
            class="block w-full form-control-sm form-select"
            @change="event => onFilterChange({ direction: event.target.value })"
          >
            <option v-for="option in availableFilters.direction" :key="option" :value="option">
              {{ option === '—' ? '—' : trans(`activity.direction.${option}`) }}
            </option>
          </select>
        </th>
        <th class="w-20">
          <select
            slot="select"
            :value="filters.flow"
            class="block w-full form-control-sm form-select"
            @change="event => onFilterChange({ flow: event.target.value })"
          >
            <option v-for="option in availableFilters.flows" :key="option" :value="option">
              {{ option === '—' ? '—' : option }}
            </option>
          </select>
        </th>
        <th>
          <div class="flex align-items-center">
            <input
              :value="filters.sku"
              class="bg-white form-control-sm form-input px-2 py-1 rounded"
              @change="event => onFilterChange({ sku: event.target.value })"
            />
            <Popper placement="top" class="light" content="Exclude 'No Results'?" hover>
              <input
                type="checkbox"
                :checked="filters.excludeNoResults"
                class="bg-white form-control-sm px-2 py-1 rounded ml-3"
                @change="event => onFilterChange({ excludeNoResults: event.target.checked })"
              />
            </Popper>
          </div>
        </th>
        <th>
          <select
            slot="select"
            :value="filters.trigger"
            class="block w-full form-control-sm form-select"
            @change="event => onFilterChange({ trigger: event.target.value })"
          >
            <option v-for="option in availableFilters.triggers" :key="option" :value="option">
              {{ option === '—' ? '—' : trans(`activity.trigger.${option}`) }}
            </option>
          </select>
        </th>
      </tr>

      <tr>
        <th class="w-20 text-center">{{ trans('common.id') }}</th>
        <th class="text-center">{{ trans('common.status') }}</th>
        <th class="text-center">{{ trans('activity.start_time') }}</th>
        <th class="text-center">{{ trans('common.application') }}</th>
        <th class="w-20 text-center">{{ trans('common.flow') }}</th>
        <th class="text-center">{{ trans('activity.log_message') }}</th>
        <th class="text-center">{{ trans('common.trigger') }}</th>
      </tr>
      </thead>

      <tbody>
      <tr v-if="loadingActivities">
        <td colspan="7">
          <div class="text-center">
            <loader class="text-60"/>
          </div>
        </td>
      </tr>
      <tr
        v-for="activity in activities"
        v-else
        :key="`${activity.id}-${activity.status}`"
        class="table-row"
        @mousedown.middle.prevent
        @click.prevent="showActivity(activity, filters.perPage, filters.page, $event)"
        @click.middle.prevent="showActivity(activity, filters.perPage, filters.page, $event, true)"
      >
        <td>
          <div class="text-center">
              <span class="whitespace-no-wrap">
                <a @click.prevent="showActivity(activity, filters.perPage, filters.page, $event, true)"
                   v-text="activity.id"/>
              </span>
          </div>
        </td>
        <td>
          <div class="text-center status">
            <i
              :class="{
                  'fa-check-square success': activity.status === 'success',
                  'fa-times-circle failed': activity.status === 'failed',
                  'fa-exclamation-triangle warning': activity.status === 'warning',
                  'fa-retweet pending': activity.status === 'pending',
                  'fa-bug debug': activity.status === 'debug',
                  'fa-pause-circle-o pause': activity.status === 'pause',
                }"
              aria-hidden="true"
              class="fa fa-2x"
            ></i>
          </div>
        </td>
        <td>
          <div class="text-center">
            {{ moment(activity.created_at).format('YYYY-MM-DD HH:mm:ss') }}
          </div>
        </td>
        <td>
          <div class="text-center">
            {{ activity.application_name }}
          </div>
        </td>
        <td>
          <div class="text-center">
            <span class="whitespace-no-wrap">{{ activity.flow_name }}</span>
          </div>
        </td>
        <td>
          <div class="text-center">
            {{ getActivityIds(activity) }}
          </div>
        </td>
        <td class="w-16">
          <div class="text-center">
              <span class="whitespace-no-wrap">
                {{ activity.trigger }}
              </span>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
    <div class="flex justify-content-between align-items-center">
      <div v-if="availableFilters && activities.length">
        <select
          name="perPage"
          class="inline-block form-control-sm form-select"
          :value="filters.perPage"
          @change="event => onFilterChange({ perPage: event.target.value })"
        >
          <option v-for="perPage in availableFilters.perPages" :key="perPage" :value="perPage">{{ perPage }}</option>
        </select>
        <label class="ml-2">{{ trans('pagination.per_page') }}</label>
      </div>
      <div v-else>{{ trans('activity.no_activities') }}</div>
      <pagination v-if="pagination" :pagination="pagination" @paginate="onFilterChange"/>
    </div>
  </div>
</template>

<script>
import Popper from 'vue3-popper';
import Pagination from '@/components/Shared/Pagination';
import Loader from '@/components/Shared/Loader';

export default {
  name: 'Activity',

  components: {
    Popper,
    Pagination,
    Loader
  },

  props: {
    userId: {
      type: [String, Number],
      required: true,
    },

    integrationId: {
      type: [String, Number],
      required: true,
    },

    integrationFlowId: {
      type: [String, Number],
      default: null,
    },

    additionalFilters: {
      type: Object,
    },

    enableWebsocket: {
      type: Boolean,
      default: true,
    },

    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadingActivities: false,
      activities: [],
      pagination: null,
      availableFilters: null,
      rangeConfig: {
        type: 'number',
      },
    };
  },

  beforeMount() {
    Promise.all([this.getActivities(), this.getFilters()])
  },

  mounted() {
    Echo.channel(this.channel).listen('UserIntegrationActivityUpdated', activity => {
      this.addToActivities(activity);
    });
  },

  beforeDestroy() {
    Echo.leave(this.channel);
  },

  computed: {
    filters() {
      const {
        page = 1,
        flow = null,
        trigger = null,
        sku = null,
        excludeNoResults = null,
        direction = null,
        status = null,
        start = null,
        end = null,
        perPage = null,
        activityId = null
      } = this.$route().params;
      return {
        page: Number(page || '1'),
        flow: flow !== '—' ? flow : null,
        trigger: trigger !== '—' ? trigger : null,
        sku: sku !== '—' ? sku : null,
        excludeNoResults: excludeNoResults == true,
        direction: direction !== '—' ? direction : null,
        status: status !== '—' ? status : null,
        start: start ? parseInt(start) : start,
        end: end ? parseInt(end) : end,
        perPage: perPage ? parseInt(perPage) : 15,
        activityId: activityId !== '—' ? activityId : null
      };
    },
    range: {
      get: function () {
        return {
          start: this.filters.start,
          end: this.filters.end,
        };
      },
      set: function (newValue) {
        this.onFilterChange({
          start: newValue ? newValue.start : null,
          end: newValue ? newValue.end : null,
        });
      },
    },
    channel() {
      return this.integrationFlowId !== null ?
        `activity.${this.userId}-${this.integrationId}-${this.integrationFlowId}`
        : `activity.${this.userId}-${this.integrationId}`;
    }
  },

  methods: {
    getActivityIds(activity) {
      const eventData = activity['event_data'] || [];
      if (activity.entity_id) {
        return activity.entity_id;
      }
      return Array.isArray(eventData) ? eventData.map(product => product.id).join(', ') : '';
    },

    getActivities() {
      this.loadingActivities = true;

      const endpoint = this.integrationFlowId === null ?
        `/api/integrations/${this.integrationId}/activity`
        : `/api/integrations/${this.integrationId}/flows/${this.integrationFlowId}/activity`

      return axios
        .get(endpoint, {
          params: {...this.filters, ...(this.additionalFilters || {})},
        })
        .then(({data}) => {
          const {data: activities, ...rest} = data.activities;
          this.activities = activities;
          this.pagination = rest;
        })
        .finally(() => this.loadingActivities = false);
    },

    getFilters() {
      const endpoint = this.integrationFlowId === null ?
        `/api/integrations/${this.integrationId}/activity/filters`
        : `/api/integrations/${this.integrationId}/flows/${this.integrationFlowId}/activity/filters`

      return axios
        .get(endpoint, {
          params: {...(this.additionalFilters || {})},
        })
        .then(({data}) => {
          this.availableFilters = data.availableFilters;
        });
    },

    onFilterChange(data) {
      const filters = {...this.filters, ...data};
      const parameters = this.$route().params

      filters['page'] = data['page'] ?? 1

      Object.keys(filters).forEach(filter => {
        delete parameters[filter]

        let filterValue = filters[filter]

        if (filterValue !== null && filterValue !== '—') {
          parameters[filter] = filterValue;
        }
      });

      this.$inertia.visit(
        this.$route(this.$route().current(), parameters),
      )
    },

    showActivity(activity, perPage, page, event, blank) {
      if (event.target.localName === 'a' && !blank) {
        return
      }

      let parameters = {
        user: this.userId,
        integration: activity.user_integration_id,
        integrationFlow: activity.user_integration_flow_id,
        activity: activity.id,
        perPage: perPage,
        page: page
      }

      let route = this.$route('dashboard.user.integrations.flows.activities.show', parameters)

      if (blank || event.ctrlKey) {
        window.open(route, '_blank')
      } else {
        this.$inertia.visit(route)
      }
    },

    checkIfIsAddToActivities(activity) {
      let isAddToActivities = true;
      ['flow', 'trigger', 'sku', 'direction', 'status'].forEach(key => {
        let valueToCheck = activity[key];
        if (key === 'sku') {
          valueToCheck = activity.entity_id;
        }

        if (
          (valueToCheck !== null && this.filters[key] !== null && !this.filters[key].includes(valueToCheck)) ||
          (valueToCheck === null && this.filters[key] !== null)
        ) {
          isAddToActivities = false;
        }
      });
      return isAddToActivities;
    },

    addToActivities(activity) {
      if (activity.status === 'debug' && !this.isAdmin) {
        return;
      }
      const activityIndex = _.findIndex(this.activities, {id: activity.id});
      if (activityIndex !== -1) {
        this.activities[activityIndex] = activity;
        this.$forceUpdate();
        return;
      }

      if (
        this.filters.flow === null &&
        this.filters.trigger === null &&
        (this.filters.sku == null || this.filters.sku === '') &&
        this.filters.direction === null &&
        this.filters.status === null
      ) {
        this.activities.unshift(activity);
      } else {
        if (this.checkIfIsAddToActivities(activity)) {
          this.activities.unshift(activity);
        }
      }
    },
  },
};
</script>

<style>
#activity_log.table thead th {
  vertical-align: middle;
}

.table-row {
  cursor: pointer;
}

.status {
  font-size: 1.6rem;
}

.failed {
  color: var(--danger);
}

.success {
  color: var(--green);
}

.pending,
.warning {
  color: #e5ce00;
}

.pause {
  color: black;
}

.debug {
  color: var(--info);
}
</style>
